import { ChecklistItemInputType, getEvaluationCore } from 'types';

import type { FlagMappingFormValue, InputOptionFormValue } from './checklistItemInputOptions';

export interface FlagMappingSelectItem {
  label: string;
  value: string;
  selected: boolean;
}

export const isFlagCreatingDecisionType = (decisionType?: string): boolean =>
  !!decisionType && decisionType !== 'approved' && decisionType !== 'approvedNoFlag';

export const buildLevelItems = (
  available: { label: string; value: string }[],
  selectedId: string | undefined,
): FlagMappingSelectItem[] => {
  const items = available.map((item) => ({
    ...item,
    selected: item.value === selectedId,
  }));

  if (selectedId && !available.some((item) => item.value === selectedId)) {
    items.unshift({
      label: 'Unavailable',
      value: selectedId,
      selected: true,
    });
  }

  return items;
};

const sanitizeMappings = (mappings: FlagMappingFormValue[] | undefined): FlagMappingFormValue[] =>
  (mappings ?? []).map((mapping) => ({
    requirementFlagSettingId: mapping.requirementFlagSettingId,
    ...(mapping.criterionId ? { criterionId: mapping.criterionId } : {}),
    ...(mapping.criterionId && mapping.problemId ? { problemId: mapping.problemId } : {}),
  }));

// Convert legacy single-ID options into editable flag mappings.
export const seedFlagMappings = (option: InputOptionFormValue): FlagMappingFormValue[] => {
  if (option.flagMappings?.length) return option.flagMappings;

  return option.requirementFlagSettingId ? [{ requirementFlagSettingId: option.requirementFlagSettingId }] : [];
};

// Keep the legacy ID in sync with the first mapping for the existing review flow.
export const buildOptionPayload = (option: InputOptionFormValue): InputOptionFormValue => {
  const { flagMappings, requirementFlagSettingId: _legacyId, ...rest } = option;
  const mappings = isFlagCreatingDecisionType(option.decisionType) ? sanitizeMappings(flagMappings) : [];
  if (mappings.length === 0) return rest;

  return {
    ...rest,
    flagMappings: mappings,
    requirementFlagSettingId: mappings[0].requirementFlagSettingId,
  };
};

// Single source of truth for whether the decision-option editor (and therefore flag mappings) is
// reachable for an input; validation, submit, and the editor's visibility must all agree on it.
export const exposesDecisionOptions = (type: string | undefined, evaluationKey?: string): boolean => {
  if (type === ChecklistItemInputType.requirement) return true;
  if (type !== ChecklistItemInputType.evaluation || !evaluationKey) return false;
  return !!getEvaluationCore(evaluationKey as Parameters<typeof getEvaluationCore>[0])?.supportsDecisionOptions;
};

// Options on inputs without the decision-option editor can still carry stale mappings in form state.
export const stripOptionFlagMappings = (option: InputOptionFormValue): InputOptionFormValue => {
  const { flagMappings: _mappings, requirementFlagSettingId: _legacyId, ...rest } = option;
  return rest;
};
